import { useCallback, useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { customerApi } from '../lib/api'
import type { StructureData } from '@/pages/it-structures/types.ts'

export interface CustomerStructureEnvelope {
  id: string
  version: number
  structure_data: StructureData
  notes: string | null
}

export interface StructureDiagnostic {
  requestedCustomerId?: string
  userId?: string
  email?: string
  existingCustomerIdsInTable?: string[]
  reason?: string
  foundVersion?: number | null
  isActive?: number | boolean | null
}

interface GetResponse {
  structure: CustomerStructureEnvelope | null
  diagnostic?: StructureDiagnostic
}

export interface PolicyViolation {
  opType: string
  detail: string
  ref?: string
}

interface SaveResponse {
  id: string
  version: number
  changes: number
  tanssTicketId: string | null
  ticketWarning: string | null
}

interface SaveVars {
  notes?: string
}

interface ServerErrorBody {
  error?: string
  code?: string
  violations?: PolicyViolation[]
}

/**
 * Lokaler Edit-Zustand der Kunden-Struktur. Der Server-Snapshot wird per
 * `useQuery` geladen; Edits leben in React-State (`draft`) und werden per
 * `save()` als komplette Struktur per `PUT` gesendet. Der Backend-Guard
 * (`validateCustomerStructurePatch`) kann die Aenderung mit HTTP 403 ablehnen
 * — der Fehler inkl. Violations wird in `lastViolations` gespiegelt, damit
 * die UI sie aufgeklappt anzeigen kann.
 */
export function useCustomerStructure() {
  const qc = useQueryClient()
  const query = useQuery<GetResponse>({
    queryKey: ['customer-it-structure'],
    queryFn: () => customerApi.get<GetResponse>('/it-structures/structure').then((r) => r.data),
  })

  const [draft, setDraft] = useState<StructureData | null>(null)
  const [dirty, setDirty] = useState(false)
  const [lastViolations, setLastViolations] = useState<PolicyViolation[] | null>(null)
  const [lastError, setLastError] = useState<string | null>(null)
  const [lastSaveSummary, setLastSaveSummary] = useState<SaveResponse | null>(null)

  // Server-Stand uebernehmen, wenn Query fertig geladen hat oder nach Save
  // aktualisiert wurde.
  useEffect(() => {
    const server = query.data?.structure?.structure_data
    if (server && !dirty) {
      setDraft(server)
    }
  }, [query.data, dirty])

  const update = useCallback((patch: Partial<StructureData>) => {
    setDraft((prev) => {
      if (!prev) return prev
      return { ...prev, ...patch }
    })
    setDirty(true)
    setLastViolations(null)
    setLastError(null)
  }, [])

  const mutation = useMutation<SaveResponse, unknown, SaveVars>({
    mutationFn: async ({ notes }) => {
      if (!draft) throw new Error('Noch keine Struktur geladen')
      const res = await customerApi.put<SaveResponse>('/it-structures/structure', {
        structure_data: draft,
        notes,
      })
      return res.data
    },
    onSuccess: (data) => {
      setLastSaveSummary(data)
      setLastViolations(null)
      setLastError(null)
      setDirty(false)
      void qc.invalidateQueries({ queryKey: ['customer-it-structure'] })
    },
    onError: (err: unknown) => {
      const response = (err as { response?: { status?: number; data?: ServerErrorBody } }).response
      const body = response?.data
      if (response?.status === 403 && body?.code === 'CUSTOMER_POLICY_VIOLATION') {
        setLastViolations(body.violations ?? [])
        setLastError(body.error ?? 'Strukturänderung nicht erlaubt')
      } else {
        setLastViolations(null)
        setLastError(body?.error ?? (err instanceof Error ? err.message : 'Speichern fehlgeschlagen'))
      }
    },
  })

  return {
    data: draft,
    serverStructure: query.data?.structure ?? null,
    diagnostic: query.data?.diagnostic ?? null,
    loading: query.isLoading,
    loadError: query.error instanceof Error ? query.error.message : null,
    dirty,
    saving: mutation.isPending,
    save: (notes?: string) => mutation.mutate({ notes }),
    lastViolations,
    lastError,
    lastSaveSummary,
    update,
    refresh: () => {
      setDirty(false)
      void qc.invalidateQueries({ queryKey: ['customer-it-structure'] })
    },
  }
}
